import { useDispatch } from 'react-redux';
import { useUploadNdaMutation } from '@api/selfServe';
import { setToastMessage } from '@stores/App/slice';
import { TOAST_TYPES } from '@constants/constants';

// Define types for the hook
interface UploadNdaResult {
  success: boolean;
  message?: string;
}

// File restrictions
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB in bytes
const ALLOWED_FILE_TYPES = ['application/pdf'];

const useUploadNda = () => {
  const [uploadNdaRequest, { isLoading, isError }] = useUploadNdaMutation();
  const dispatch = useDispatch();

  const validateFile = (file: File): { valid: boolean; message?: string } => {
    // Check file size
    if (file.size > MAX_FILE_SIZE) {
      return {
        valid: false,
        message: 'File size exceeds the maximum limit of 5MB',
      };
    }

    // Check file type
    if (!ALLOWED_FILE_TYPES.includes(file.type)) {
      return {
        valid: false,
        message: 'Only PDF files are allowed',
      };
    }

    return { valid: true };
  };

  const showErrorToast = (message: string): void => {
    dispatch(
      setToastMessage({
        heading: 'File Upload Failed',
        message,
        type: TOAST_TYPES.ERROR,
      }),
    );
  };

  const uploadNda = async (
    selfServeId: string | undefined,
    ndaFile: File | undefined,
  ): Promise<UploadNdaResult> => {
    // Check for required parameters
    if (!selfServeId) {
      const message = 'Missing required parameter: selfServeId';
      showErrorToast(message);
      return { success: false, message };
    }

    if (!ndaFile) {
      const message = 'No file selected';
      showErrorToast(message);
      return { success: false, message };
    }

    // Validate file before uploading
    const validation = validateFile(ndaFile);
    if (!validation.valid) {
      showErrorToast(validation.message || 'Invalid file');
      return { success: false, message: validation.message };
    }

    try {
      // Create FormData object for the file upload
      const formData = new FormData();
      formData.append('nda', ndaFile);

      // Call the mutation with the selfServeId and formData
      await uploadNdaRequest({
        selfServeId,
        nda: formData,
      }).unwrap();

      return { success: true };
    } catch (err) {
      console.error('Error uploading NDA:', err);

      // Show error toast
      const errorMessage =
        err instanceof Error
          ? err.message
          : 'An unexpected error occurred while uploading the file';

      showErrorToast(errorMessage);

      return { success: false, message: errorMessage };
    }
  };

  return { uploadNda, isLoading, isError };
};

export default useUploadNda;
